import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Receipt, Download, Send, DollarSign, FileText } from 'lucide-react';
import { COMPANY } from '../../../config/company';
import CompanyLogo from '../../CompanyLogo';

const LeadInvoiceTab = ({
  lead,
  language,
  companyInfo,
  invoiceStatusOptions,
  paymentTermsOptions,
  formatCurrency,
  generatingPdf,
  invoiceRef,
  updateInvoice,
  syncInvoiceFromEstimate,
  generateInvoicePDF,
  emailPdf,
  getAllLicenses
}) => {
  return (
    <>
      <Card>
        <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center">
              <Receipt className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Factura' : 'Invoice'}
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={syncInvoiceFromEstimate} className="text-xs">
                <DollarSign className="h-3 w-3 mr-1" />
                {language === 'es' ? 'Sincronizar' : 'Sync from Estimate'}
              </Button>
              <Button size="sm" variant="secondary" onClick={generateInvoicePDF} disabled={generatingPdf} className="text-xs">
                <Download className="h-3 w-3 mr-1" />
                PDF
              </Button>
              {lead.client_email && (
                <Button 
                  size="sm" 
                  onClick={() => emailPdf('invoice')} 
                  disabled={generatingPdf}
                  style={{ backgroundColor: COMPANY.primaryColor }}
                  className="text-white text-xs"
                >
                  <Send className="h-3 w-3 mr-1" />
                  {language === 'es' ? 'Enviar' : 'Email'}
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'es' ? 'Número de Factura' : 'Invoice Number'}</Label>
              <Input 
                value={lead.invoice?.invoice_number || ''} 
                onChange={(e) => updateInvoice('invoice_number', e.target.value)} 
              />
            </div>
            <div>
              <Label>{language === 'es' ? 'Estado' : 'Status'}</Label>
              <Select 
                value={lead.invoice?.status || 'draft'} 
                onValueChange={(v) => updateInvoice('status', v)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {invoiceStatusOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'es' ? 'Fecha de Factura' : 'Invoice Date'}</Label>
              <Input 
                type="date" 
                value={lead.invoice?.invoice_date || ''} 
                onChange={(e) => updateInvoice('invoice_date', e.target.value)} 
              />
            </div>
            <div>
              <Label>{language === 'es' ? 'Fecha de Vencimiento' : 'Due Date'}</Label>
              <Input 
                type="date" 
                value={lead.invoice?.due_date || ''} 
                onChange={(e) => updateInvoice('due_date', e.target.value)} 
              />
            </div>
          </div>
          <div>
            <Label>{language === 'es' ? 'Términos de Pago' : 'Payment Terms'}</Label>
            <Select 
              value={lead.invoice?.payment_terms || 'net_30'} 
              onValueChange={(v) => updateInvoice('payment_terms', v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {paymentTermsOptions.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{language === 'es' ? 'Descripción' : 'Description'}</Label>
            <Textarea 
              value={lead.invoice?.description || ''} 
              onChange={(e) => updateInvoice('description', e.target.value)} 
              rows={2} 
            />
          </div>
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div>
              <Label>{language === 'es' ? 'Monto Total' : 'Total Amount'}</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={lead.invoice?.total_amount || 0} 
                onChange={(e) => updateInvoice('total_amount', e.target.value)} 
                className="text-lg font-bold" 
              />
            </div>
            <div>
              <Label>{language === 'es' ? 'Monto Pagado' : 'Amount Paid'}</Label>
              <Input 
                type="number" 
                step="0.01" 
                value={lead.invoice?.amount_paid || 0} 
                onChange={(e) => updateInvoice('amount_paid', e.target.value)} 
                className="text-lg text-green-600" 
              />
            </div>
            <div>
              <Label>{language === 'es' ? 'Saldo Pendiente' : 'Balance Due'}</Label>
              <div className="text-2xl font-bold text-red-600 mt-1">
                {formatCurrency(lead.invoice?.balance_due || 0)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Preview */}
      <Card className="mt-4">
        <CardHeader className="bg-gray-800 text-white">
          <CardTitle className="flex items-center text-sm">
            <FileText className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Vista Previa de Factura' : 'Invoice Preview'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div ref={invoiceRef} className="bg-white p-6 text-sm">
            {/* Company Header */}
            <div style={{ backgroundColor: companyInfo.primaryColor }} className="text-white p-4 -mx-6 -mt-6 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="bg-white p-2 rounded">
                    {companyInfo.logo ? (
                      <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto" />
                    ) : (
                      <CompanyLogo className="h-10 w-auto" />
                    )}
                  </div>
                  <div>
                    <h1 className="text-xl font-bold">{companyInfo.name}</h1>
                    <p className="text-amber-100 text-xs">{getAllLicenses() || companyInfo.tagline}</p>
                  </div>
                </div>
                <div className="text-right text-xs">
                  <p>{companyInfo.address}</p>
                  <p>{companyInfo.city}, {companyInfo.state} {companyInfo.zip}</p>
                  <p>{companyInfo.phone} | {companyInfo.email}</p>
                </div>
              </div>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-gray-800">
                {language === 'es' ? 'FACTURA' : 'INVOICE'}
              </h2>
              <p style={{ color: companyInfo.primaryColor }} className="font-mono">
                {lead.invoice?.invoice_number}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6 text-xs">
              <div className="border rounded p-3">
                <p className="font-semibold text-gray-500 mb-1">
                  {language === 'es' ? 'FACTURAR A' : 'BILL TO'}
                </p>
                <p className="font-bold">{lead.client_name}</p>
                <p>{lead.client_address}</p>
                <p>{lead.client_phone}</p>
                <p>{lead.client_email}</p>
              </div>
              <div className="border rounded p-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-gray-500">{language === 'es' ? 'Fecha' : 'Date'}:</p>
                    <p className="font-semibold">{lead.invoice?.invoice_date}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{language === 'es' ? 'Vence' : 'Due'}:</p>
                    <p className="font-semibold">{lead.invoice?.due_date}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">{language === 'es' ? 'Términos' : 'Terms'}:</p>
                    <p className="font-semibold">
                      {paymentTermsOptions.find(t => t.value === lead.invoice?.payment_terms)?.label}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">{language === 'es' ? 'Referencia' : 'Ref'}:</p>
                    <p className="font-semibold">{lead.proposal_id || lead.estimate_number}</p>
                  </div>
                </div>
              </div>
            </div>

            <table className="w-full border mb-6 text-sm">
              <thead style={{ backgroundColor: companyInfo.primaryColor }} className="text-white">
                <tr>
                  <th className="text-left p-3">{language === 'es' ? 'Descripción' : 'Description'}</th>
                  <th className="text-right p-3 w-32">{language === 'es' ? 'Monto' : 'Amount'}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-gray-50">
                  <td className="p-3">{lead.invoice?.description || 'Services as per Estimate'}</td>
                  <td className="p-3 text-right font-bold">{formatCurrency(lead.invoice?.total_amount)}</td>
                </tr>
              </tbody>
            </table>

            <div className="flex justify-end mb-6">
              <div className="w-64 border rounded overflow-hidden">
                <div className="flex justify-between p-2 border-b">
                  <span>{language === 'es' ? 'Total' : 'Total'}:</span>
                  <span className="font-bold">{formatCurrency(lead.invoice?.total_amount)}</span>
                </div>
                <div className="flex justify-between p-2 border-b bg-green-50">
                  <span>{language === 'es' ? 'Pagado' : 'Paid'}:</span>
                  <span className="text-green-600">-{formatCurrency(lead.invoice?.amount_paid)}</span>
                </div>
                <div 
                  className="flex justify-between p-3 text-lg font-bold" 
                  style={{ backgroundColor: companyInfo.primaryColor, color: 'white' }}
                >
                  <span>{language === 'es' ? 'SALDO' : 'BALANCE DUE'}:</span>
                  <span>{formatCurrency(lead.invoice?.balance_due)}</span>
                </div>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500 border-t pt-4">
              <p className="font-semibold mb-1">
                {language === 'es' ? 'Gracias por su preferencia' : 'Thank you for your business!'}
              </p>
              <p>{companyInfo.website} • {companyInfo.email} • {companyInfo.phone}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default LeadInvoiceTab;
